import { useEffect, useRef } from 'react';
import { useWatch, type Control } from 'react-hook-form';
import debug from 'debug';

// Compression
import { compressSessionStorage, decompressSessionStorage } from '../utils/sessionStorage';

// Debug namespace for cache operations
const logCache = debug('rois:cache:form');

/**
 * ✅ FIX BUG #21 + #22: Smart cache hook that prevents race conditions
 *
 * PROBLEMA ORIGINAL (BUG #21):
 * - useWatch({ control }) puede retornar valores vacíos en el primer render
 * - Esto sobrescribía datos válidos del servidor con arrays vacíos
 *
 * PROBLEMA BUG #22:
 * - La protección bloqueaba updates LEGÍTIMOS cuando el usuario quita operarios
 * - La validación seguía viendo datos antiguos en sessionStorage
 *
 * SOLUCIÓN:
 * 1. Saltar el primer render (hasMounted ref)
 * 2. Comparar con sessionStorage existente antes de guardar
 * 3. Solo aplicar protección en renders tempranos (effectCount <= 3)
 * 4. Después del render 3, permitir eliminar datos legítimamente
 * 5. Preservar orderId/actionId si existen en sessionStorage pero no en formValues
 */
export const useCacheFormInSessionStorage = <T extends Record<string, unknown>>(
  formKey: string,
  control: Control<T>
) => {
  // Track mount count for debugging
  const mountCount = useRef(0);
  const effectCount = useRef(0);
  // ✅ FIX: Skip first render to let React Hook Form initialize defaultValues
  const hasMounted = useRef(false);

  // Cache the new communication form on changes to keep them when loading the app again
  const formValues = useWatch({ control });

  // Log on mount
  useEffect(() => {
    mountCount.current += 1;
    logCache('🔄 [useCacheForm] MOUNT #%d - formKey: %s', mountCount.current, formKey);
    return () => {
      logCache('🔴 [useCacheForm] UNMOUNT - formKey: %s', formKey);
    };
  }, [formKey]);

  useEffect(() => {
    effectCount.current += 1;

    // ✅ FIX BUG #21: Skip the FIRST render to allow React Hook Form to initialize
    if (!hasMounted.current) {
      hasMounted.current = true;
      logCache('⏭️ [useCacheForm] SKIPPING first render - formKey: %s (waiting for RHF initialization)', formKey);
      return;
    }

    // 🔬 DEBUG BUG #21: Detailed logging for operators data
    const operatorsTable = (formValues as any)?.operatorsTable;
    const vehicleTable = (formValues as any)?.vehicleTable;
    const filteredOps = Array.isArray(operatorsTable)
      ? operatorsTable.filter((x: any) => x)
      : [];
    const vehicleKeys = Object.keys(vehicleTable || {});

    logCache('═══════════════════════════════════════════════════════════════');
    logCache('💾 [useCacheForm] EFFECT #%d - formKey: %s', effectCount.current, formKey);
    logCache('📊 formValues keys: %O', Object.keys(formValues || {}));
    logCache('👷 operatorsTable: %O', {
      isArray: Array.isArray(operatorsTable),
      rawLength: operatorsTable?.length || 0,
      filteredLength: filteredOps.length,
      sample: filteredOps.slice(0, 2).map((op: any) => ({
        id: op?.id,
        name: op?.operator?.name || op?.name,
      })),
    });
    logCache('🚗 vehicleTable: %O', {
      hasData: !!vehicleTable,
      keys: vehicleKeys,
    });

    // ✅ FIX BUG #21: Compare with existing sessionStorage before overwriting
    const existingData = sessionStorage.getItem(formKey);
    if (existingData) {
      try {
        const parsed = JSON.parse(decompressSessionStorage(existingData));
        const existingOps = parsed?.operatorsTable?.filter?.((x: any) => x) || [];
        const existingVehicleKeys = Object.keys(parsed?.vehicleTable || {});

        // ⚠️ PROTECTION: Don't overwrite if we'd lose operators or vehicle data
        const wouldLoseOperators = existingOps.length > 0 && filteredOps.length === 0;
        const wouldLoseVehicles = existingVehicleKeys.length > 0 && vehicleKeys.length === 0;

        // ✅ FIX BUG #22: Solo aplicar protección en renders tempranos (race condition de RHF)
        // Después de 3 renders, el usuario tiene control total para eliminar datos
        const isEarlyRender = effectCount.current <= 3;

        if (isEarlyRender && (wouldLoseOperators || wouldLoseVehicles)) {
          logCache('🛡️ [useCacheForm] BLOCKED early render #%d - protecting against RHF race condition', effectCount.current);
          logCache('   existing: %d ops, %d vehicles | new: %d ops, %d vehicles',
            existingOps.length, existingVehicleKeys.length, filteredOps.length, vehicleKeys.length);
          logCache('═══════════════════════════════════════════════════════════════');
          return; // Don't save - would lose important data due to race condition
        }

        // Después de renders tempranos, registrar que el usuario eliminó datos intencionalmente
        if (wouldLoseOperators || wouldLoseVehicles) {
          logCache('📝 [useCacheForm] User intentionally cleared data at effect #%d - allowing save', effectCount.current);
          logCache('   existing: %d ops, %d vehicles | new: %d ops, %d vehicles',
            existingOps.length, existingVehicleKeys.length, filteredOps.length, vehicleKeys.length);
        }
      } catch (e) {
        // If we can't parse existing data, proceed with save
        logCache('⚠️ [useCacheForm] Could not parse existing sessionStorage, proceeding with save');
      }
    }

    logCache('═══════════════════════════════════════════════════════════════');

    // ✅ FIX: Preservar orderId/actionId si existen en sessionStorage pero no en formValues
    let dataToSave = formValues;
    if (existingData) {
      try {
        const parsed = JSON.parse(decompressSessionStorage(existingData));
        if (parsed?.orderId && !(formValues as any)?.orderId) {
          logCache('🛡️ [useCacheForm] Preservando orderId/actionId del sessionStorage existente');
          dataToSave = {
            ...formValues,
            orderId: parsed.orderId,
            actionId: parsed.actionId,
          } as typeof formValues;
        }
      } catch (e) { /* ignore */ }
    }

    const jsonString = JSON.stringify(dataToSave);
    const compressed = compressSessionStorage(jsonString);
    sessionStorage.setItem(formKey, compressed);

    logCache('✅ Saved to sessionStorage: %s (compressed size: %d chars)', formKey, compressed.length);
  }, [formValues, formKey]);
};
